import React from "react";
import { useTranslation } from "react-i18next";
import { UPPER_FDI, LOWER_FDI, toothLabel, nextToothState } from "../lib/dental";

// Interactive dental chart. `value` is an FDI-keyed map { "11": "abutment"|"pontic" }.
// Click cycles: none -> abutment (color A) -> pontic (color B) -> none.
const Tooth = ({ fdi, state, numbering, upper, onClick, readOnly, suggested }) => {
  const base =
    "relative flex items-center justify-center border text-[11px] font-semibold transition-colors select-none w-8 h-10 sm:w-9 sm:h-11";
  const shape = upper ? "rounded-t-lg rounded-b-sm" : "rounded-b-lg rounded-t-sm";
  let style =
    state === "abutment"
      ? "border-indigo-500 bg-indigo-500 text-white shadow-sm"
      : state === "pontic"
      ? "border-amber-500 bg-amber-400 text-amber-950 shadow-sm"
      : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:bg-indigo-50";
  // Pending Smart-Bridge suggestion on an unselected tooth: dashed amber, animated.
  if (suggested && !state) {
    style = "border-2 border-dashed border-amber-500 bg-amber-100 text-amber-800 animate-pulse";
  }
  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={onClick}
      data-testid={`tooth-${fdi}`}
      data-state={state || "none"}
      data-suggested={suggested ? "true" : "false"}
      title={toothLabel(fdi, numbering)}
      className={`${base} ${shape} ${style} ${readOnly ? "cursor-default" : "cursor-pointer"}`}
    >
      {toothLabel(fdi, numbering)}
    </button>
  );
};

const Arch = ({ teeth, value, numbering, upper, onToggle, readOnly, suggestedSet }) => (
  <div className="flex items-end justify-center gap-0.5">
    {teeth.map((fdi, i) => (
      <React.Fragment key={fdi}>
        {i === teeth.length / 2 && <div className="mx-1 h-10 w-px bg-slate-300" aria-hidden />}
        <Tooth
          fdi={fdi}
          state={value[String(fdi)]}
          numbering={numbering}
          upper={upper}
          readOnly={readOnly}
          suggested={suggestedSet.has(String(fdi))}
          onClick={() => onToggle(fdi)}
        />
      </React.Fragment>
    ))}
  </div>
);

export const Odontogram = ({ value = {}, onChange, numbering = "fdi", readOnly = false, suggested = [], testid = "odontogram" }) => {
  const { t } = useTranslation();
  const suggestedSet = new Set((suggested || []).map(String));

  const toggle = (fdi) => {
    if (readOnly) return;
    const key = String(fdi);
    const next = { ...value };
    const ns = nextToothState(value[key]);
    if (ns) next[key] = ns;
    else delete next[key];
    onChange?.(next);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={testid}>
      {!readOnly && (
        <p className="mb-2 text-center text-[11px] text-muted-foreground">{t("restoration.selectTeethHint")}</p>
      )}
      <div className="space-y-1 overflow-x-auto pb-1">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{t("odontogram.upper")}</p>
        <Arch teeth={UPPER_FDI} value={value} numbering={numbering} upper onToggle={toggle} readOnly={readOnly} suggestedSet={suggestedSet} />
        <Arch teeth={LOWER_FDI} value={value} numbering={numbering} upper={false} onToggle={toggle} readOnly={readOnly} suggestedSet={suggestedSet} />
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{t("odontogram.lower")}</p>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-[11px]">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-indigo-500" /> {t("odontogram.abutment")}</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-400" /> {t("odontogram.pontic")}</span>
        {suggestedSet.size > 0 && (
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-2 border-dashed border-amber-500 bg-amber-100" /> {t("odontogram.suggested")}</span>
        )}
      </div>
    </div>
  );
};
